import { useState } from 'react';
import { SectionCard } from '@/components/DataList';
import { ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  FlashMessage,
  FormActions,
  Input,
  Loading,
  Money,
  VisibilitySelect,
} from '@/components/ui';
import { formatNumber, str } from '@/utils/format';

type MembershipForm = {
  id: number;
  name: string;
  min_amount_usd: string;
  profit_percent: string;
  is_active: number;
  sort_order: string;
};

const EMPTY_FORM: MembershipForm = {
  id: 0,
  name: '',
  min_amount_usd: '',
  profit_percent: '',
  is_active: 1,
  sort_order: '0',
};

export function MembershipsScreen() {
  const state = useAsync(
    () => admin.memberships(),
    (p) => pickList(p, 'data', 'memberships', 'plans', 'items'),
  );

  const [form, setForm] = useState<MembershipForm>(EMPTY_FORM);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!form.name.trim()) {
      setMsg('اسم العضوية مطلوب.');
      return;
    }
    if (form.min_amount_usd.trim() === '' || form.profit_percent.trim() === '') {
      setMsg('المبلغ المطلوب ونسبة الربح مطلوبان.');
      return;
    }
    setBusy(true);
    const body: JsonMap = {
      name: form.name.trim(),
      min_amount_usd: Number(form.min_amount_usd),
      profit_percent: Number(form.profit_percent),
      is_active: form.is_active,
      sort_order: Number(form.sort_order) || 0,
    };
    if (form.id > 0) body.id = form.id;
    const res = await admin.saveMembership(body);
    setBusy(false);
    setMsg(errorMessage(res, res.success ? 'تم حفظ العضوية.' : 'تعذر الحفظ.'));
    if (res.success) {
      setForm(EMPTY_FORM);
      state.reload();
    }
  }

  async function disable(id: unknown) {
    if (!window.confirm('تعطيل العضوية ونقل مستخدميها إلى العضوية الأساسية؟')) return;
    const res = await admin.deleteMembership({ id });
    setMsg(errorMessage(res, res.success ? 'تم تعطيل العضوية.' : 'تعذر التعطيل.'));
    if (res.success) state.reload();
  }

  function edit(row: JsonMap) {
    setForm({
      id: Number(row.id) || 0,
      name: str(row.name, ''),
      min_amount_usd: str(row.min_amount_usd, ''),
      profit_percent: str(row.profit_percent, ''),
      is_active: Number(row.is_active ?? 1) ? 1 : 0,
      sort_order: str(row.sort_order ?? '0'),
    });
    setMsg('');
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  const list = state.data ?? [];

  return (
    <ScreenFrame
      mode="admin"
      icon="shield"
      title="إدارة العضويات"
      subtitle="تحدد نسبة الربح المستخدمة في منتجات API وفئات شحن الرصيد. أقل عضوية مفعلة تُمنح تلقائياً للمستخدم الجديد."
      actions={
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> تحديث
        </Button>
      }
    >
      <FlashMessage message={msg} />

      <SectionCard title={form.id ? 'تعديل عضوية' : 'إضافة عضوية'} icon={<Icon name="users" size={24} />}>
        <div className="form-grid two-columns">
          <Input
            label="اسم العضوية"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="VIP 1"
          />
          <Input
            label="المبلغ المطلوب بالدولار"
            value={form.min_amount_usd}
            onChange={(e) => setForm((f) => ({ ...f, min_amount_usd: e.target.value }))}
            inputMode="decimal"
            placeholder="50"
          />
          <Input
            label="نسبة الربح %"
            value={form.profit_percent}
            onChange={(e) => setForm((f) => ({ ...f, profit_percent: e.target.value }))}
            inputMode="decimal"
            placeholder="5"
          />
          <VisibilitySelect
            value={form.is_active}
            onChange={(v) => setForm((f) => ({ ...f, is_active: Number(v) }))}
          />
          <Input
            label="الترتيب"
            value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
            inputMode="numeric"
          />
        </div>
        <FormActions>
          <Button type="button" disabled={busy} onClick={() => void save()}>
            {form.id ? 'حفظ تعديل العضوية' : 'إضافة العضوية'}
          </Button>
          {form.id > 0 ? (
            <Button type="button" variant="secondary" onClick={() => setForm(EMPTY_FORM)}>
              إلغاء التعديل
            </Button>
          ) : null}
        </FormActions>
      </SectionCard>

      <SectionCard title="العضويات الحالية" icon={<Icon name="chart" size={24} />}>
        {list.length === 0 ? (
          <Empty title="لا عضويات" hint="أضف خطة عضوية واحدة على الأقل." />
        ) : (
          <div className="membership-grid">
            {list.map((m) => {
              const active = Number(m.is_active ?? 1) === 1;
              return (
                <article
                  key={str(m.id)}
                  className={`membership-card ${active ? '' : 'disabled'}`.trim()}
                >
                  <div>
                    <strong>{str(m.name)}</strong>
                    <span>{formatNumber(m.profit_percent)}% ربح</span>
                  </div>
                  <p>
                    المبلغ المطلوب: <Money value={m.min_amount_usd} suffix="$" />
                  </p>
                  <small>{formatNumber(m.users_count ?? 0)} مستخدم</small>
                  <div className="action-row wrap">
                    <Button type="button" variant="secondary" onClick={() => edit(m)}>
                      تعديل
                    </Button>
                    {active ? (
                      <Button type="button" variant="danger" onClick={() => void disable(m.id)}>
                        تعطيل
                      </Button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}
